package com.devi3east.bashbuddy.features.common.util

import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.requiredWidthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.DialogProperties
import kotlinx.datetime.*

const val DAY_MS = 86_400_000L

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ModernCalendarPicker(
    initialInstant: Instant? = null,
    onDateSelected: (Instant) -> Unit,
    onDismiss: () -> Unit
) {
    // ----------------------
    // 1) Epoch-normalize incoming instant
    // ----------------------
    val rawMillis = initialInstant?.toEpochMilliseconds()
        ?: Clock.System.now().toEpochMilliseconds()

    val epochDays = (rawMillis / DAY_MS).toInt()
    val date = LocalDate.fromEpochDays(epochDays) // date derived from epoch-days (UTC day)
    val normalizedSelectedMillis = date.toEpochDays().toLong() * DAY_MS // midnight UTC for that date

    // ----------------------
    // 2) displayedMonth candidate: first day-of-month midnight UTC + 1 day nudge (empirical)
    // ----------------------
    val firstOfMonth = LocalDate(date.year, date.monthNumber, 1)
    val firstOfMonthMillis = firstOfMonth.toEpochDays().toLong() * DAY_MS
    val displayedMonthMillisCandidate = firstOfMonthMillis + DAY_MS // +1 day nudge

    // ----------------------
    // 3) Create DatePicker state (selection + displayed month)
    // ----------------------
    val state = rememberDatePickerState(
        initialSelectedDateMillis = normalizedSelectedMillis,
        initialDisplayedMonthMillis = displayedMonthMillisCandidate
    )

    // ----------------------
    // 4) Custom title (overrides default month dropdown label)
    //    We compute month/year from state.displayedMonthMillis using epoch-days math so
    //    the label is timezone-agnostic and matches the calendar grid.
    // ----------------------
    val title: @Composable () -> Unit = {
        val dispMillis = state.displayedMonthMillis
        val dispEpochDays = (dispMillis / DAY_MS).toInt()
        val dispDate = LocalDate.fromEpochDays(dispEpochDays)
        val monthName = dispDate.month.name.lowercase().replaceFirstChar { it.uppercase() }
        val label = "$monthName ${dispDate.year}"

        // Render title similar to default style
        Text(
            text = label,
            style = MaterialTheme.typography.titleLarge,
            modifier = Modifier.padding(16.dp)
        )
    }

    // ----------------------
    // 5) Headline composable "May 9, 2026" (dynamic)
    // ----------------------
    val headline: @Composable () -> Unit = {
        val selMillis = state.selectedDateMillis
        if (selMillis == null) {
            Text("Select A Date", Modifier.padding(16.dp))
        } else {
            val selEpochDays = (selMillis / DAY_MS).toInt()
            val selDate = LocalDate.fromEpochDays(selEpochDays)
            val formatted = buildString {
                append(selDate.month.name.lowercase().replaceFirstChar { it.uppercase() })
                append(" ")
                append(selDate.dayOfMonth)
                append(", ")
                append(selDate.year)
            }
            Text(
                text = formatted,
                style = MaterialTheme.typography.headlineMedium,
                modifier = Modifier.padding(16.dp)
            )
        }
    }

    // ----------------------
    // 7) UI - DatePicker in Dialog (use our title + headline)
    // ----------------------
    AlertDialog(
        onDismissRequest = {
            onDismiss()
        },
        properties = DialogProperties(usePlatformDefaultWidth = false),
        text = {
            DatePicker(
                state = state,
                showModeToggle = false,
                title = title,        // override default month dropdown label with timezone-agnostic title
                headline = headline,  // keep your dynamic headline
                modifier = Modifier
                    .fillMaxWidth()
                    .requiredWidthIn(min = 380.dp)
                    .heightIn(min = 360.dp),
                dateFormatter = utcDateFormatter()
            )
        },
        confirmButton = {
            Button(onClick = {
                val millisSel = state.selectedDateMillis
                if (millisSel != null) {
                    val selEpoch = (millisSel / DAY_MS).toInt()
                    val selDate = LocalDate.fromEpochDays(selEpoch)
                    val instant = Instant.fromEpochMilliseconds(selDate.toEpochDays().toLong() * DAY_MS)

                    onDateSelected(instant)
                } else {
                    log("⚠️ CONFIRM pressed but selectedDateMillis is null")
                }
                onDismiss()
            },
                colors = ButtonDefaults.buttonColors(
                    containerColor = MaterialTheme.colorScheme.primary,
                    contentColor = MaterialTheme.colorScheme.onPrimary
                ),
                shape = RoundedCornerShape(12.dp)
            ) { Text("OK") }
        },
        dismissButton = {
            TextButton(onClick = {
                onDismiss()
            }) { Text("Cancel", color = MaterialTheme.colorScheme.onSurfaceVariant) }
        },
        shape = RoundedCornerShape(24.dp)
    )
}

expect fun log(msg: String)

@OptIn(ExperimentalMaterial3Api::class)
fun utcDateFormatter(): DatePickerFormatter =
    object : DatePickerFormatter {

        override fun formatMonthYear(monthMillis: Long?, locale: CalendarLocale): String? {
            if (monthMillis == null) return null

            val epochDays = (monthMillis / DAY_MS).toInt()
            val date = LocalDate.fromEpochDays(epochDays)

            val month = date.month.name.lowercase().replaceFirstChar { it.uppercase() }
            return "$month ${date.year}"
        }

        override fun formatDate(
            dateMillis: Long?,
            locale: CalendarLocale,
            forContentDescription: Boolean
        ): String? {
            if (dateMillis == null) return null

            val epochDays = (dateMillis / DAY_MS).toInt()
            val date = LocalDate.fromEpochDays(epochDays)

            val month = date.month.name.lowercase().replaceFirstChar { it.uppercase() }
            return "$month ${date.dayOfMonth}, ${date.year}"
        }
    }
